import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement } from 'react'
import useEntity from '../hooks/useEntity'
import { UserDto } from '../config/userConfig'
import { DocumentContext, FolderType, IDefaultFolderDto } from '../config/documentConfig'
import { DocumentType } from '../helpers/documentHelper'
import { FilePartyRole } from '../config/filePartyConfig'
import { DimensionType } from '../components/Form/Accounting/DimensionFields'
import { LegalAidCertificateSettings } from '../components/Form/LegalAidCertificate/LegalAidCertificateForm'
import { AuthorizationGroupDto } from '../components/Combobox/AuthorizationGroupCombobox'
import { Country, Currency, Language, LegalStructure, VatType } from '../config/i18nConfig'
import { AttributeDefinitionDto } from '../hooks/useAttributes'
import { DisbursementType } from '../config/registrationConfig'
import { Billable } from '../config/registration/timeConfig'
import { Gender } from '../config/contactConfig'
import { InvoiceMessage } from '../config/finance/invoiceConfig'

export enum ModuleType {
    Accounting = 'ACCOUNTING',
    Attributes = 'ATTRIBUTES',
    Authorization = 'AUTHORIZATION',
    BudgetMonitoring = 'BUDGET_MONITORING',
    Docket = 'DOCKET',
    FamilyLaw = 'FAMILY_LAW',
    Injury = 'INJURY',
    Invoices = 'INVOICES',
    Insolvency = 'INSOLVENCY',
    LegalAid = 'LEGAL_AID',
    Mollie = 'MOLLIE',
    ProofMe = 'PROOF_ME',
    Reports = 'REPORTS',
    Tasks = 'TASKS',
    TimeCategories = 'TIME_CATEGORIES',
    TimeTracking = 'TIME_TRACKING',
    WorkInProgress = 'WORK_IN_PROGRESS',
}

export type ModuleDto = {
    type: ModuleType
    fileModule: boolean
    defaultActiveForFile: boolean
}

export enum Accounting {
    Exact = 'EXACT',
    Twinfield = 'TWINFIELD',
}

export type DashboardDto = {
    enabled?: boolean
    showRecentFiles?: boolean
}

export type DocumentDto = {
    uri: string
    label: string
    type: DocumentType
    context: DocumentContext
    name?: string
    folder?: FolderType
    role?: FilePartyRole | string
}

export type FilePartyRoleDto = {
    type: FilePartyRole | string
    label: string
}

export enum FileRateType {
    File = 'FILE',
    Tier = 'TIER',
}

export type FileScaffoldDto = {
    billable: Billable
    language: string
    currency: string
    vatType: string
    fileRate: FileRateDto
}

export type FileRateDto = {
    type: FileRateType
    hourRate: string
}

export enum SortDirection {
    Ascending = 'ASC',
    Descending = 'DESC',
}

export type FileSortDto = {
    direction: SortDirection
    field: string
}

export type UnitDto = {
    key: string
    name: string
    enabled: boolean
    accounting: Accounting
    dimensions: DimensionType[]
}

export type GlobalContextType = {
    settings?: SettingsDto
    units?: UnitDto[]
    users?: UserDto[]
    countries?: Country[]
    currencies?: Currency[]
    languages?: Language[]
    legalStructures?: LegalStructure[]
    vatTypes?: VatType[]
    authorizationGroups?: AuthorizationGroupDto[]
    attributeDefinitions?: AttributeDefinitionDto[]
    disbursementTypes?: DisbursementType[]
    reset?: () => void
}

export enum InvoiceRecipientEnum {
    Client = 'CLIENT',
    Debtor = 'DEBTOR',
}

export type InvoiceRecipientDto = {
    client: InvoiceRecipientEnum
    debtor: InvoiceRecipientEnum
}

export type InsolvencySetting = {
    factorEstate: string
    rateEstate: string
    roles?: InsolvencyRole[]
}

type InsolvencyRole = {
    key: string
    name: string
}

export type LocalizedValueSetting<K = string, V = string> = {
    key: K
    value: V
}

export enum TimeSuggestionDescriptionOptionEnum {
    EmailIn = 'EMAIL_IN',
    EmailOut = 'EMAIL_OUT',
}

export enum TimeCategoryDefaultEnum {
    DefaultEmailIn = 'DEFAULT_EMAIL_IN',
    DefaultEmailOut = 'DEFAULT_EMAIL_OUT',
    Default = 'DEFAULT',
}

export type TimeCategory = {
    key: string
    name: string
    module?: ModuleType
    defaultDescription?: string
    defaultType?: TimeCategoryDefaultEnum
}

export type SettingsDto = {
    modules?: ModuleDto[]
    cocNumber?: number
    dashboard?: DashboardDto
    documents?: DocumentDto[]
    fileExchangeDisplayName?: string
    fileSharepointDisplayName?: string
    filePartyRoles?: FilePartyRoleDto[]
    fileReference?: any
    fileScaffold?: FileScaffoldDto
    fileSort?: FileSortDto
    folders?: IDefaultFolderDto[]
    ibanNumber?: string
    invoiceEmailBcc?: string[]
    invoiceEmailMessages?: InvoiceMessage[]
    invoicePaymentCurrency?: boolean
    invoicePaymentMessages?: InvoiceMessage[]
    invoiceRecipient?: InvoiceRecipientDto
    invoiceNumber?: any
    legalAidCertificate?: LegalAidCertificateSettings
    locality?: string
    partySalutation?: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]
    partyAttentionOf?: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]
    termInvoice?: number
    termPayment?: number
    vatNumber?: string
    insolvency?: InsolvencySetting
    timeCategories?: TimeCategory[]
    timeDivisor?: number
    timeSuggestionDescription?: LocalizedValueSetting<
        string,
        LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]
    >[]
}

const GlobalContext = createContext<GlobalContextType>(null)

const getter = (url: string) => () => ({ method: 'GET', url })

export function GlobalProvider(props: PropsWithChildren): ReactElement {
    const [{ data: settings }, , resetSettings] = useEntity<SettingsDto>(getter('/setting'), false)
    const [{ data: units }, , resetUnits] = useEntity<UnitDto[]>(getter('/setting/unit'), false)
    const [{ data: users }, , resetUsers] = useEntity<UserDto[]>(getter('/user'), false)
    const [{ data: countries }, , resetCountries] = useEntity<Country[]>(getter('/country/list'), false)
    const [{ data: currencies }, , resetCurrencies] = useEntity<Currency[]>(getter('/currency/list'), false)
    const [{ data: languages }, , resetLanguages] = useEntity<Language[]>(getter('/language/list'), false)
    const [{ data: legalStructures }, , resetLegalStructures] = useEntity<LegalStructure[]>(
        getter('/legalstructure/list'),
        false,
    )
    const [{ data: vatTypes }, , resetVatTypes] = useEntity<VatType[]>(getter('/vattype/list'), false)
    const [{ data: authorizationGroups }, , resetAuthorizationGroups] = useEntity<AuthorizationGroupDto[]>(
        getter('/authorization/group'),
        false,
        result =>
            result.data.sort((a: AuthorizationGroupDto, b: AuthorizationGroupDto) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            ),
    )
    const [{ data: attributeDefinitions }, , resetAttributeDefinitions] = useEntity<AttributeDefinitionDto[]>(
        getter('/setting/attribute-definition'),
        false,
        result =>
            result.data.sort(
                (a: AttributeDefinitionDto, b: AttributeDefinitionDto) => (a.position || 1000) - (b.position || 1000),
            ),
    )
    const [{ data: disbursementTypes }, , resetDisbursementTypes] = useEntity<DisbursementType[]>(
        getter('/registration/disbursement/type'),
        false,
        result => result.data.sort(),
    )

    return (
        <GlobalContext.Provider
            value={{
                settings: settings,
                units: units || [],
                users: users || [],
                countries: countries || [],
                currencies: currencies || [],
                languages: languages || [],
                legalStructures: legalStructures || [],
                vatTypes: vatTypes || [],
                authorizationGroups: authorizationGroups || [],
                attributeDefinitions: attributeDefinitions || [],
                disbursementTypes: disbursementTypes || [],
                reset: () => {
                    resetSettings()
                    resetUnits()
                    resetUsers()
                    resetCountries()
                    resetCurrencies()
                    resetLanguages()
                    resetLegalStructures()
                    resetVatTypes()
                    resetAttributeDefinitions()
                    resetAuthorizationGroups()
                    resetDisbursementTypes()
                },
            }}
        >
            {props.children}
        </GlobalContext.Provider>
    )
}

export default GlobalContext
